import { useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { UnknownType, UserResponse } from 'stream-chat';

import { Tooltip } from '@bizzabo/design-system';

import useRealTimeMeetingsEnabled from '../../../../hooks/useRealTimeMeetingsEnabled';
import useScheduleMeetingsEnabled from '../../../../hooks/useScheduleMeetingsEnabled';
import useTypeTracking from '../../../../../analytics';
import useMeetButtonTooltip from './useMeetButtonTooltip';
import { showVideoAtom } from '../../../../../video/atoms';
import { incomingCallAtom } from '../../../../../notifications/videoNotifications/atoms';
import MeetDropdown from './MeetDropdown';
import MeetMainButton from './MeetMainButton';
import { useMessages } from '../../../contexts/MessagesContext';

interface meetButtonProps {
    channelStatus: number;
    guestUser: UserResponse<UnknownType>;
    onMeetNowClick: () => void;
    onScheduleClick: () => void;
}

const MeetButton: React.FC<meetButtonProps> = ({
    channelStatus,
    guestUser,
    onMeetNowClick,
    onScheduleClick,
}) => {
    const { eventUniqueName } = useMessages();
    const { data: isRealTimeMeetingsEnabled, isLoading: isLoadingRealTimeEnabled } =
        useRealTimeMeetingsEnabled(eventUniqueName);
    const { data: isScheduleMeetingsEnabled, isLoading: isLoadingScheduledEnabled } =
        useScheduleMeetingsEnabled(eventUniqueName);

    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);
    const { tooltipText } = useMeetButtonTooltip({ channelStatus, guestUser });
    const { trackClickEvent } = useTypeTracking();
    const [isMeetCalled, setIsMeetCalled] = useState(false);
    const isShowVideo = useAtomValue(showVideoAtom);
    const incomingCall = useAtomValue(incomingCallAtom);
    const isMeetBtnDisabled =
        (!!tooltipText || isMeetCalled || incomingCall.hasIncomingCall) &&
        !isScheduleMeetingsEnabled;
    const isMeetNowDisabled =
        (!!tooltipText || isMeetCalled || incomingCall.hasIncomingCall) &&
        isScheduleMeetingsEnabled;

    const shouldUseDropdown = isRealTimeMeetingsEnabled && isScheduleMeetingsEnabled;

    useEffect(() => {
        if (!isShowVideo) {
            setIsMeetCalled(false);
        }
    }, [isShowVideo]);

    const isLoading = isLoadingRealTimeEnabled || isLoadingScheduledEnabled;
    if (isLoading) {
        return null;
    }

    const sendMeetNowEvent = () => {
        trackClickEvent({
            subCategory: 'MEETING_INVITATION_TYPE_CHOSEN',
            description: {
                meeting_type: 'meet_now',
                button_disabled: isMeetNowDisabled,
                button_type: 'main_chat_button',
            },
        });
    };

    const startRealTimeMeeting = () => {
        setIsMeetCalled(true);
        onMeetNowClick();
        sendMeetNowEvent();
    };

    const handleMeetButtonClick = () => {
        if (isRealTimeMeetingsEnabled && tooltipText) {
            setIsTooltipOpen(true);
            return;
        }

        if (shouldUseDropdown || isMeetBtnDisabled) {
            return;
        }

        if (!isScheduleMeetingsEnabled) {
            startRealTimeMeeting();
            return;
        }

        if (!isRealTimeMeetingsEnabled) {
            onScheduleClick();
        }
    };

    if (shouldUseDropdown) {
        return (
            <MeetDropdown
                meetButton={
                    <MeetMainButton
                        isMeetBtnDisabled={isMeetBtnDisabled}
                        shouldUseDropdown={shouldUseDropdown}
                        handleMeetButtonClick={handleMeetButtonClick}
                    />
                }
                tooltipText={tooltipText}
                isMeetNowDisabled={isMeetNowDisabled}
                onMeetNowHandle={startRealTimeMeeting}
                onScheduleClick={onScheduleClick}
            />
        );
    }

    return (
        <Tooltip
            open={isTooltipOpen}
            anchorRef={anchorRef}
            setOpen={setIsTooltipOpen}
            text={tooltipText}
            boundariesElement="viewport"
        >
            <div ref={anchorRef}>
                <MeetMainButton
                    isMeetBtnDisabled={isMeetBtnDisabled}
                    shouldUseDropdown={shouldUseDropdown}
                    handleMeetButtonClick={handleMeetButtonClick}
                />
            </div>
        </Tooltip>
    );
};

export default MeetButton;
